import { MutableRefObject, RefCallback, RefObject, useCallback, useEffect, useRef } from 'react';

type TInputRef = RefObject<HTMLInputElement> | RefCallback<HTMLInputElement> | undefined;

const assignRef = (ref: TInputRef, node: HTMLInputElement | null) => {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as MutableRefObject<HTMLInputElement | null>).current = node;
  }
};

/**
 * Keeps the DOM-only `indeterminate` property of a checkbox in sync with a React prop.
 *
 * `indeterminate` has no HTML attribute, so React cannot render it
 * (https://github.com/facebook/react/issues/1798) and it has to be written to the node
 * imperatively. Two things can put the node out of sync with the prop, and each is handled
 * where it happens:
 *
 * 1. the prop changes — the effect below writes the new value;
 * 2. the checkbox is clicked — the HTML spec's checkbox "pre-click activation steps" clear
 *    `indeterminate` before toggling checkedness, and React does not restore it the way it
 *    restores `checked`. `applyIndeterminate` is therefore called again from the change
 *    handler, so the prop stays the single source of truth even when the consumer's handler
 *    does not re-render.
 *
 * Nothing else clears the property, so the write is deliberately *not* repeated on every
 * render: a list of checkboxes would pay a DOM write per item per render for no reason.
 * A passive effect is enough because the visible state does not depend on this property —
 * the `is-indeterminate` class and the icon are both rendered from the prop, so the first
 * paint is already correct and only assistive technology and `:indeterminate` read the node.
 *
 * @param isIndeterminate the desired state of the property
 * @param inputRef an optional external ref, merged with the internal one
 */
const useIndeterminate = (isIndeterminate: boolean, inputRef?: TInputRef) => {
  const nodeRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = useCallback<RefCallback<HTMLInputElement>>(
    (node) => {
      nodeRef.current = node;
      assignRef(inputRef, node);
    },
    [inputRef],
  );

  const applyIndeterminate = useCallback(() => {
    if (nodeRef.current) {
      nodeRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Runs on mount and whenever the prop changes — `applyIndeterminate` is memoised on it.
  useEffect(() => {
    applyIndeterminate();
  }, [applyIndeterminate]);

  return { setInputRef, applyIndeterminate };
};

export default useIndeterminate;
